import { useEffect, useState, type CSSProperties } from 'react';

/**
 * Measure an image's natural aspect (w/h) from its (already-displayed, so
 * cached) URL. Returns `null` until known. `naturalWidth/Height` are readable
 * cross-origin without tainting — only pixel reads need CORS, which we don't
 * do. Pass `null`/`undefined` to skip (e.g. when the aspect is known upfront).
 */
export function useImageAspect(src?: string | null): number | null {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    if (!src) return;
    let alive = true;
    const probe = new window.Image();
    const read = () => {
      if (alive && probe.naturalWidth > 0) {
        setAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    probe.onload = read;
    probe.src = src;
    if (probe.complete) read();
    return () => {
      alive = false;
    };
  }, [src]);
  return aspect;
}

/**
 * Absolute positioning for the rectangle an `object-contain object-bottom`
 * (centre-x) image of `aspect` occupies inside a box of `boxAspect`. Image
 * narrower than the box (aspect ≤ boxAspect) → fits by height; wider → fits by
 * width. Always centred horizontally and bottom-anchored (mirrors
 * `object-bottom`), so the card's real edges are framed regardless of size.
 */
export function containedRectStyle(
  aspect: number,
  boxAspect: number,
): CSSProperties {
  const fitByHeight = aspect <= boxAspect;
  return {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translateX(-50%)',
    width: fitByHeight ? `${(aspect / boxAspect) * 100}%` : '100%',
    height: fitByHeight ? '100%' : `${(boxAspect / aspect) * 100}%`,
  };
}
